import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  getCatalogCollapseStatus,
  selectKP11SelectCartCentersNApps,
  updateCatalogCollapseStatus,
  selectAlphaSEOFromRecurly,
  setAlphaSEOFromRecurly,
  selectDefaultCartCentersNApps,
  selectItem,
} from '../../../../../../app-store/kp11CartSlice'
import {
  CardPlanBodyStyle,
  CardPlanWebsiteUrlStyle,
  CardPlanDomainWrapperStyle,
  IsExternalDomainRadioStyle,
  CardPlanHeaderStyle,
} from '../kp-11Link-catalog-cards-style'
import {CardPlanItemWrapStyle} from '../../../kp-11Link-reusable-components/kp-plan-radio-input-style'
import {PlanRadioInput} from '../../../kp-11Link-reusable-components/kp-plan-radio-input'
import {FloatingInput} from '../../../kp-11Link-reusable-components/kp-reusable-input'
import {kP11LinkService} from '../../../kp-11Link-utility/kp11-service'
import {useAuthClient} from '../../../../../../utils/use-auth-client'
import {useAsync} from '../../../../../../utils/use-async'
import {KPSEOAddons} from '../../../kp-11Link-reusable-components/kp-seo-addons'
import {
  buildCartEntity,
  getItemFromCart,
} from '../../../kp-11Link-utility/kp11-utility'
import {fetchPlanIcon} from '../../../../../../utils/fetch-plan-icon'
import {Button, Icon} from '@thryvlabs/maverick'
import {noteInfo} from '../../../../../../assets'
import {ProductNamePriceComponent} from '../../../../kp-card-type/product-name-price-component'

export const applyUpgradePricing = ({
  builtItem, // result from buildCartEntity
  selectedDefaultItem, // existing subscription in cart (if any)
}) => {
  if (!selectedDefaultItem) return builtItem

  const fullAmount = builtItem.unitAmount * builtItem.quantity

  // If default amount already covers (or exceeds) the new amount,
  // keep default item but carry addons forward
  if (
    selectedDefaultItem.amount === fullAmount ||
    selectedDefaultItem.amount > fullAmount
  ) {
    return {...selectedDefaultItem, addons: builtItem.addons}
  }

  // Otherwise charge only the delta (upgrade)
  const delta =
    selectedDefaultItem.amount < fullAmount
      ? fullAmount - selectedDefaultItem.amount
      : selectedDefaultItem.amount

  return {
    ...builtItem,
    amount: delta,
    isAlreadyAdded: selectedDefaultItem?.isAlreadyAdded,
    isUpgraded: selectedDefaultItem?.isAlreadyAdded,
  }
}

export const SEOProposalIdComponent = ({
  seoData,
  setSeoData,
  required,
  seoSubDetails,
  handlePropIdValidatedNUpdated,
}) => {
  const client = useAuthClient()
  const {run} = useAsync({status: 'pending'})
  const dispatch = useDispatch()
  const [localProposalId, setLocalProposalId] = useState(
    seoData?.alphaProposalId || '',
  )

  const lastInputTimeRef = useRef(null)
  const debounceTimerRef = useRef(null)

  const validateProposalId = async proposalId => {
    if (!proposalId) return

    setSeoData(prev => ({
      ...prev,
      loading: true,
      isError: false,
      helperText: '',
    }))

    try {
      const response = await kP11LinkService.validateSEOProposalId(
        run,
        client,
        proposalId,
      )

      if (response?.isSuccess) {
        const {price, domain, isValidProposal} = response?.proposalInfo ?? {}
        setSeoData({
          alphaProposalId: proposalId ?? '',
          alphaAmount: price ?? '',
          alphaDomain: domain ?? '',
          isAlphaProposalIdValidated: Boolean(isValidProposal),
          isError: false,
          helperText: '',
          loading: false,
          isSeoUpdated:
            proposalId === seoSubDetails?.customFields?.alphaProposalId,
        })
        dispatch(
          setAlphaSEOFromRecurly({
            alphaProposalId: proposalId ?? '',
            alphaDomain: domain ?? '',
            alphaAmount: price ?? '',
          }),
        )

        // handlePropIdValidatedNUpdated is triggered by useEffect with fresh seoData
      } else {
        setSeoData({
          alphaProposalId: '',
          alphaAmount: '',
          alphaDomain: '',
          isAlphaProposalIdValidated: false,
          isExternalDomain: false,
          isError: true,
          helperText: 'Invalid Proposal ID',
          loading: false,
          isSeoUpdated: true,
        })
        dispatch(
          setAlphaSEOFromRecurly({
            alphaProposalId: '',
            alphaDomain: '',
            alphaAmount: '',
          }),
        )
      }
    } catch (err) {
      setSeoData({
        alphaProposalId: '',
        alphaAmount: '',
        alphaDomain: '',
        isAlphaProposalIdValidated: false,
        isError: true,
        helperText: `The entered proposal ID is not valid. Try again with a correct ID.`,
        loading: false,
        isSeoUpdated: true,
      })
      dispatch(
        setAlphaSEOFromRecurly({
          alphaProposalId: '',
          alphaDomain: '',
          alphaAmount: '',
        }),
      )
    }
  }

  // validation logic for length or delay
  useEffect(() => {
    if (!localProposalId) {
      setSeoData(prev => ({
        ...prev,
        isError: false,
        helperText: '',
        isAlphaProposalIdValidated: false,
      }))
      return
    }

    clearTimeout(debounceTimerRef.current)

    const now = Date.now()
    lastInputTimeRef.current = now

    if (localProposalId.trim().length === 8) {
      // Immediate validation if exactly 8 characters
      validateProposalId(localProposalId.trim())
    } else {
      // Delay-based validation
      debounceTimerRef.current = setTimeout(() => {
        const timeSinceLastInput = Date.now() - lastInputTimeRef.current
        if (timeSinceLastInput >= 1000 && localProposalId.trim().length < 8) {
          validateProposalId(localProposalId.trim())
        }
      }, 1000)
    }

    return () => clearTimeout(debounceTimerRef.current)
  }, [localProposalId])

  const handleBlur = () => {
    const proposalId = localProposalId.trim()

    if (required && !proposalId) {
      setSeoData(prev => ({
        ...prev,
        isError: true,
        helperText: 'Proposal ID is required',
        isAlphaProposalIdValidated: false,
      }))
      return
    }

    validateProposalId(proposalId)
  }

  return (
    <FloatingInput
      label="Enter Proposal ID"
      type="text"
      value={localProposalId}
      onChange={e => setLocalProposalId(e.target.value)}
      onBlur={handleBlur}
      isValid={seoData?.isAlphaProposalIdValidated}
      isError={seoData?.isError}
      helperText={seoData?.helperText}
      loading={seoData?.loading}
      required={required}
    />
  )
}

/* ----------------------- External Domain Radio ----------------------- */

export const IsExternalDomainRadio = ({seoData, setSeoData, disabled}) => {
  const dispatch = useDispatch()
  const alphaSEOFromRecurly = useSelector(selectAlphaSEOFromRecurly)
  const handleChange = e => {
    setSeoData({isExternalDomain: e.target.value})
    dispatch(
      setAlphaSEOFromRecurly({
        ...alphaSEOFromRecurly,
        isExternalDomain: e.target.value,
      }),
    )
    // Do we need to handle the yes or no based API calls asper BUSREQ-11972
  }

  return (
    <IsExternalDomainRadioStyle>
      {['yes', 'no'].map(value => (
        <label
          key={value}
          className={`ed-radio-label-wrap ${disabled ? 'disabled' : ''}`}
        >
          <input
            className={`ed-radio-input ${disabled ? 'disabled' : ''}`}
            type="radio"
            name="isExternalDomain"
            value={value}
            checked={seoData.isExternalDomain === value}
            onChange={handleChange}
            disabled={disabled}
          />
          <span className="ed-radio-label">
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        </label>
      ))}
    </IsExternalDomainRadioStyle>
  )
}

/* ----------------------- SEO Catalog Card ----------------------- */
export const KP11LinkSeoCardComponent = ({
  selectedCountry = 'US',
  groupKey,
  items,
  selectedDefaultItem,
  onSelectItem,
  groupCustomFields,
  onGroupCustomFieldsUpdate,
  disabled,
  addedToCart,
}) => {
  // Get selectedItem from structured cart
  const alphaSEOFromRecurly = useSelector(selectAlphaSEOFromRecurly)
  const cartCentersAndApps = useSelector(selectKP11SelectCartCentersNApps)
  const defaultCartCentersNApps = useSelector(selectDefaultCartCentersNApps)
  const cardCollapseStatus = useSelector(getCatalogCollapseStatus)
  const selectedItem = getItemFromCart({
    cartCentersAndApps,
    groupKey,
    items,
  })
  const seoCode = items[0]?.code
  const defaultSeoItem =
    defaultCartCentersNApps?.apps?.find(a => a.code === seoCode) || null
  const dispatch = useDispatch()

  // Capture original Recurly values at mount so we can detect genuine user changes
  // vs. auto-validation of the same existing values when the card expands.
  const originalProposalIdRef = useRef(
    alphaSEOFromRecurly?.alphaProposalId || '',
  )
  // Existing SEO monthly price from Recurly (alphaSEOFromRecurly.alphaAmount).
  // Used to compute the correct upgrade delta since buildCartEntity always returns
  // amount = 0 for SEO (catalog unitAmount = 0, quantity carries the price).
  const originalAmountRef = useRef(
    Number(alphaSEOFromRecurly?.alphaAmount) || 0,
  )

  const [seoData, setSeoData] = useState({
    alphaProposalId: alphaSEOFromRecurly?.alphaProposalId ?? '',
    isAlphaProposalIdValidated:
      groupCustomFields?.isAlphaProposalIdValidated ?? false,
    alphaAmount: alphaSEOFromRecurly?.alphaAmount ?? '',
    alphaDomain: alphaSEOFromRecurly?.alphaDomain ?? '',
    isExternalDomain: alphaSEOFromRecurly?.isExternalDomain ?? '',
    isError: groupCustomFields?.isError ?? false,
    helperText: groupCustomFields?.helperText ?? '',
    loading: groupCustomFields?.loading ?? false,
  })

  const [seoAddons, setSeoAddons] = useState(() => {
    let initialAddons = selectedItem?.addons || []
    if (initialAddons.length === 0 && cartCentersAndApps?.apps) {
      const pb = cartCentersAndApps.apps.find(
        a => a.code === 'addon-5-seopb-m2m',
      )
      const kw = cartCentersAndApps.apps.find(
        a => a.code === 'addon-5-seokey-m2m',
      )
      if (pb || kw) {
        initialAddons = []
        if (pb) initialAddons.push(pb)
        if (kw) initialAddons.push(kw)
      }
    }
    return initialAddons
  })

  const setPartialSeoData = partial =>
    setSeoData(prev => ({...prev, ...partial}))

  useEffect(() => {
    onGroupCustomFieldsUpdate({...seoData, addons: seoAddons})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    seoData.alphaProposalId,
    seoData.isAlphaProposalIdValidated,
    seoData.isExternalDomain,
    seoAddons,
  ])

  useEffect(() => {
    if (seoData?.isAlphaProposalIdValidated) {
      handlePropIdValidatedNUpdated()
    }
  }, [
    seoData?.alphaAmount,
    seoData?.isAlphaProposalIdValidated,
    seoData?.alphaProposalId,
  ])

  useEffect(() => {
    let initialAddons = selectedItem?.addons || []
    if (initialAddons.length === 0 && cartCentersAndApps?.apps) {
      const pb = cartCentersAndApps.apps.find(
        a => a.code === 'addon-5-seopb-m2m',
      )
      const kw = cartCentersAndApps.apps.find(
        a => a.code === 'addon-5-seokey-m2m',
      )
      if (pb || kw) {
        initialAddons = []
        if (pb) initialAddons.push(pb)
        if (kw) initialAddons.push(kw)
      }
    }
    setSeoAddons(initialAddons)
  }, [selectedItem, cartCentersAndApps?.apps])

  const isPlanDisabled = (disabled, selectedItem, item, seoData) => {
    const isItemAllreadyAdded = item?.isAlreadyAdded
    const isdisabled =
      disabled ||
      isItemAllreadyAdded ||
      !seoData?.isAlphaProposalIdValidated ||
      !['yes', 'no'].includes(seoData?.isExternalDomain)
    return isdisabled
  }

  const isActionDisabled =
    disabled ||
    !seoData?.isAlphaProposalIdValidated ||
    !['yes', 'no'].includes(seoData?.isExternalDomain)

  const handleSeoAddonChange = updatedAddons => {
    setSeoAddons(updatedAddons)
    // if (!selectedItem) return
    // const updatedItem = {...selectedItem, addons: updatedAddons}
    // onSelectItem(updatedItem)
  }

  // Updated to use buildCartEntity + applyUpgradePricing
  const handlePropIdValidatedNUpdated = propId => {
    // selectedItem may be null when the existing SEO subscription lacks groupKey
    // in the cart (loaded from Recurly). Fall back to finding by code.
    const seoCartItem =
      selectedItem ||
      [...(cartCentersAndApps?.apps || [])].find(item => item.code === seoCode)
    if (!seoCartItem) return
    const originalItem =
      items.find(i => i.code === seoCartItem.code) || items[0]

    const builtItem = buildCartEntity({
      kind: 'item',
      source: originalItem,
      selectedCountry,
      quantity: Number(seoData?.alphaAmount) || 1,
      customFields: seoCartItem?.customFields ?? {},
      previousItem: seoCartItem,
      keepAddons: true,
    })

    // Use the ref to compare against the original Recurly proposal ID.
    // The apps-path in getAccountCentersAndApps does NOT set customFields on
    // defaultSeoItem, so defaultSeoItem?.customFields?.alphaProposalId is always
    // undefined — making a direct comparison always "changed". The ref captures
    // the real Recurly value at mount time.
    const isExistingOrder =
      seoCartItem?.isAlreadyAdded || defaultSeoItem?.isAlreadyAdded
    const proposalIdChanged =
      isExistingOrder &&
      !!originalProposalIdRef.current &&
      seoData?.alphaProposalId !== originalProposalIdRef.current

    // Skip dispatch when the existing subscription's proposal ID hasn't actually
    // changed — prevents overwriting "Existing" with $0 on card expand/auto-validate.
    if (isExistingOrder && !proposalIdChanged) return

    // buildCartEntity always produces amount = 0 for SEO because the catalog
    // unitAmount = 0 (quantity carries the proposal price). Compute the correct
    // cart amount directly using the captured original Recurly amount.
    const newSEOAmount = Number(seoData?.alphaAmount) || 0
    const existingAmount = originalAmountRef.current
    const isUpgrade =
      isExistingOrder && proposalIdChanged && newSEOAmount > existingAmount
    const itemAmount = isUpgrade
      ? newSEOAmount
      : isExistingOrder
      ? existingAmount
      : newSEOAmount

    // Dispatch directly to avoid the wrapper's handleSelectItem which collapses
    // the card via updateParentCollapseStatus when the item is not yet in cartItems.
    dispatch(
      selectItem({
        item: {
          ...builtItem,
          addons: seoAddons,
          amount: itemAmount,
          isAlreadyAdded: isExistingOrder || builtItem.isAlreadyAdded || false,
          isUpgraded: isUpgrade,
        },
      }),
    )
  }

  return (
    <>
      <div
        className={`d-flex flex-column w-100 ${
          disabled ? 'disable-with-opacity' : ''
        }`}
      >
        <CardPlanHeaderStyle
          onClick={() => {
            dispatch(updateCatalogCollapseStatus('aeo & seo'))
          }}
        >
          <div className="card-heading justify-content-between w-100">
            <div className="d-flex flex-row align-items-center gap-4">
              <div className="card-icon">
                <img
                  src={fetchPlanIcon(items[0].name).icon}
                  alt="plan logo"
                  className="options-logo rounded"
                />
              </div>
              <div className="card-title-text">{items[0].name}</div>
            </div>
            <div>
              <Icon
                type={'solid'}
                variant={
                  cardCollapseStatus['aeo & seo'] ? 'caretDown' : 'caretRight'
                }
                height={'15'}
                width={'15'}
                color={'#FF6100'}
              />
            </div>
          </div>
        </CardPlanHeaderStyle>
        {cardCollapseStatus['aeo & seo'] && (
          <CardPlanBodyStyle>
            <CardPlanWebsiteUrlStyle>
              <SEOProposalIdComponent
                seoData={seoData}
                setSeoData={setPartialSeoData}
                required={true}
                seoSubDetails={selectedItem}
                handlePropIdValidatedNUpdated={handlePropIdValidatedNUpdated}
              />
            </CardPlanWebsiteUrlStyle>

            <CardPlanDomainWrapperStyle>
              {seoData.isAlphaProposalIdValidated && (
                <>
                  <div className="seo-domain-label">
                    Is{' '}
                    <span className="seo-domain-url">
                      {seoData?.alphaDomain || '___'}
                    </span>{' '}
                    a Thryv hosted website?
                  </div>

                  <div className="seo-domain-option">
                    <IsExternalDomainRadio
                      seoData={seoData}
                      setSeoData={setPartialSeoData}
                      disabled={!seoData.isAlphaProposalIdValidated}
                    />
                  </div>
                </>
              )}

              {seoData.isAlphaProposalIdValidated &&
                seoData?.isExternalDomain === 'no' && (
                  <div className="seo-domain-note">
                    <div className="seo-domain-note-icon">
                      <img
                        src={noteInfo}
                        alt="Note info"
                        className="note-info-icon"
                      />
                    </div>
                    <div className="seo-domain-note-text">
                      <strong>Please Note:</strong> External site requires the
                      client to provide website content management system &
                      hosting access at onboarding. SEO order is subjecct to
                      cancellation if access is not provided at onboarding to
                      avoid billing client for inactive SEO campaign.
                    </div>
                  </div>
                )}
            </CardPlanDomainWrapperStyle>

            <div className="card-plan-subText">Select a subscription plan</div>

            <CardPlanItemWrapStyle>
              {items.length > 1 ? (
                items.map(item => (
                  <PlanRadioInput
                    key={item.code}
                    checked={selectedItem?.code === item.code}
                    item={item}
                    customFields={seoData}
                    disabled={isPlanDisabled(
                      disabled,
                      selectedItem,
                      item,
                      seoData,
                    )}
                    onChange={() => {}}
                  />
                ))
              ) : (
                <ProductNamePriceComponent
                  cardDetail={items[0]}
                  isIconRequired={false}
                  // isNameRequired={false}
                  customFields={seoData}
                  disabled={isPlanDisabled(
                    disabled,
                    selectedItem,
                    items[0],
                    seoData,
                  )}
                  country={selectedCountry}
                />
              )}

              {/* Placeholder for future keyword and powerboost components */}
              <KPSEOAddons
                selectedDefaultItem={defaultSeoItem}
                selectedItem={{...(selectedItem || {}), addons: seoAddons}}
                disabled={disabled || !seoData?.isAlphaProposalIdValidated}
                onSEOAddonChange={handleSeoAddonChange}
                selectedCountry={selectedCountry}
                seoEntitlements={items[0]?.entitlements || []}
              />
            </CardPlanItemWrapStyle>
            <div className="d-flex w-100 justify-content-center">
              <Button
                variant="primary"
                disabled={isActionDisabled}
                onClick={() => {
                  const updatedItem = buildCartEntity({
                    kind: 'item',
                    source: items[0],
                    selectedCountry,
                    quantity: Number(seoData?.alphaAmount) || 1,
                    customFields: seoData,
                    previousItem: selectedItem,
                    keepAddons: true,
                  })
                  onSelectItem({...updatedItem, addons: seoAddons})
                }}
              >
                Add to Cart
              </Button>
            </div>
          </CardPlanBodyStyle>
        )}
      </div>
    </>
  )
}
